import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, useToast,
} from '@chakra-ui/react';
import { rutasApi, localidadesApi, Ruta, Localidad } from '../../../api/serviciosApi';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">{children}</Text>
  </Box>
);
const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

const FormularioRuta: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [codigo, setCodigo] = useState(data?.codigo || '');
  const [nombre, setNombre] = useState(data?.nombre || '');
  const [descripcion, setDescripcion] = useState(data?.descripcion || '');
  const [diasServicio, setDiasServicio] = useState(data?.diasServicio || '');
  const [localidad, setLocalidad] = useState(data?.localidad || '');
  const [colonia, setColonia] = useState(data?.colonia || '');
  const [estatus, setEstatus] = useState(data?.estatus || 'activo');
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    localidadesApi.list().then(setLocalidades).catch(() => setLocalidades([]));
  }, []);

  const handleGuardar = async () => {
    if (!nombre) {
      toast({ title: 'Campo obligatorio', description: 'El nombre de la ruta es requerido.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const loc = localidades.find((l) => l.descripcion === localidad);
      const body: Partial<Ruta> = {
        codigo, nombre, descripcion, diasServicio, localidad,
        localidadId: loc?.id, colonia, estatus,
      };
      if (modo === 'edit') await rutasApi.update(data.id, body);
      else await rutasApi.create(body);
      toast({ title: modo === 'edit' ? 'Ruta actualizada' : 'Ruta registrada', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Error al guardar.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view') {
    const d: Ruta = data || {};
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos de la Ruta</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="CÓDIGO" value={d.codigo} />
          <Field label="ESTATUS" value={<Badge colorScheme={d.estatus === 'activo' ? 'green' : 'gray'}>{d.estatus}</Badge>} />
          <Box gridColumn={{ md: 'span 2' }}><Field label="NOMBRE" value={d.nombre} /></Box>
          <Field label="LOCALIDAD" value={d.localidad} />
          <Field label="COLONIA" value={d.colonia} />
          <Field label="DÍAS DE SERVICIO" value={d.diasServicio} />
        </SimpleGrid>
        {d.descripcion && (<><SectionTitle>Descripción</SectionTitle><Text fontSize="sm" whiteSpace="pre-wrap">{d.descripcion}</Text></>)}
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos de la Ruta</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Código</FormLabel>
          <Input placeholder="Ej. R-01" value={codigo} onChange={(e) => setCodigo(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
            <option value="activo">activo</option>
            <option value="inactivo">inactivo</option>
          </Select>
        </FormControl>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Nombre</FormLabel>
          <Input placeholder="Ej. Ruta Centro" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona localidad" value={localidad} onChange={(e) => setLocalidad(e.target.value)}>
            {localidades.map((l) => <option key={l.id} value={l.descripcion}>{l.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Colonia</FormLabel>
          <Input value={colonia} onChange={(e) => setColonia(e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Días de servicio</FormLabel>
          <Input placeholder="Ej. Lunes, Miércoles, Viernes" value={diasServicio} onChange={(e) => setDiasServicio(e.target.value)} />
        </FormControl>
      </SimpleGrid>
      <SectionTitle>Descripción</SectionTitle>
      <Textarea rows={3} placeholder="Detalle de la ruta..." value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">Guardar</Button>
      </HStack>
    </VStack>
  );
};

export default FormularioRuta;
